import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { HardDrive, Loader2, Unplug, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { DriveCredentials } from "@/components/DriveCredentials";
import { DriveFolders } from "@/components/DriveFolders";

export const DriveCard = ({ settings, onSettings }) => {
  const [st, setSt] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.get("/drive/status"), api.get("/drive/config")])
    .then(([a, b]) => { setSt(a.data); setCfg(b.data); });
  useEffect(() => {
    load();
    const r = new URLSearchParams(window.location.search).get("drive");
    if (r === "ok") toast.success("Google Drive conectado");
    if (r === "error" || r === "scope") toast.error("Não foi possível conectar o Google Drive");
    if (r) window.history.replaceState({}, "", "/settings");
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao iniciar conexão"); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Desconectar o Google Drive? Os arquivos já enviados continuam no Drive.")) return;
    await api.post("/drive/disconnect");
    toast.success("Google Drive desconectado");
    load();
  };

  if (!st || !cfg) return null;
  return (
    <Card data-testid="drive-card">
      <CardContent className="p-5 space-y-5">
        <div className="flex flex-wrap sm:flex-nowrap items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-sky-100 dark:bg-sky-950 flex items-center justify-center text-sky-700 dark:text-sky-200 shrink-0">
            <HardDrive className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <div className="font-semibold flex items-center gap-2">
              Google Drive
              {st.connected
                ? <Badge className="bg-emerald-600 text-[10px]" data-testid="drive-status-badge">Conectado</Badge>
                : <Badge variant="secondary" className="text-[10px]" data-testid="drive-status-badge">Desconectado</Badge>}
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">
              Envie anexos das conversas para o Drive da empresa, organizados como você definir abaixo.
              {st.connected && <> Conta: <b data-testid="drive-email">{st.email}</b> · {st.uploaded_count} arquivo(s) enviados.</>}
            </div>
          </div>
          {st.connected ? (
            <Button variant="outline" size="sm" onClick={disconnect} data-testid="drive-disconnect-button"><Unplug className="w-4 h-4 mr-1" /> Desconectar</Button>
          ) : (
            <Button size="sm" onClick={connect} disabled={!st.configured || busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="drive-connect-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conectar Google Drive"}
            </Button>
          )}
        </div>
        {!st.configured && (
          <div className="text-xs flex items-start gap-2 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg" data-testid="drive-not-configured">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Preencha o Client ID e o Client Secret abaixo. No Google Cloud, cadastre a origem e o URI de redirecionamento exatamente como mostrados.</span>
          </div>
        )}
        <DriveCredentials key={`${cfg.client_id}-${cfg.secret_hint}`} cfg={cfg} connected={st.connected} onSaved={() => load()} />
        <div className="border-t border-border" />
        <DriveFolders key={`${cfg.folder_template}-${cfg.root_folder_name}`} cfg={cfg} onSaved={() => load()} />
        {st.connected && (
          <div className="flex items-center justify-between gap-4 border-t border-border pt-4">
            <div className="text-sm">
              <div className="font-medium">Enviar automaticamente arquivos recebidos</div>
              <div className="text-xs text-muted-foreground">Cada arquivo recebido pelo WhatsApp vai direto para a pasta definida.</div>
            </div>
            <Switch checked={!!settings.drive_auto_upload} onCheckedChange={(v) => onSettings({ drive_auto_upload: v })} data-testid="drive-auto-toggle" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
